import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import publicService from '../../services/publicService';
import memberService from '../../services/memberService';
import '../../styles/AddProfessionalListing.css';
import { PUBLIC_URLS } from '../config/apiConfig';

function AddProfessionalListing() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    qualification: '',
    expertise: '',
    email: '',
    mobile: '',
    linkedin: '',
    address: '',
    city: '',
    state: '',
    additionalInfo: ''
  });
  
  // Add a ref to track if the component is mounted
  const isMounted = useRef(true);
  
  // Add loading state to track when data is being fetched
  const [isLoading, setIsLoading] = useState(true);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberPrice, setMemberPrice] = useState(7500);
  const [nonMemberPrice, setNonMemberPrice] = useState(10000);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [profileDataLoaded, setProfileDataLoaded] = useState(false);
  const [initialDataSet, setInitialDataSet] = useState(false);
  
  // Add a state for displaying the member name immediately
  const [memberDisplayName, setMemberDisplayName] = useState('Member');
  
  // Add these refs at the top of your component
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const addressInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const stateInputRef = useRef(null);
  
  // Add this state to track attempts
  const [loadAttempts, setLoadAttempts] = useState(0);
  
  // Add this state at the top with other state variables
  const [formLocked, setFormLocked] = useState(false);
  const originalDataRef = useRef(null);
  
  // Add state to track if form data was ever loaded successfully
  const [dataWasSuccessfullyLoaded, setDataWasSuccessfullyLoaded] = useState(false);
  // Add last saved form data ref to detect unintentional clears
  const lastSavedFormDataRef = useRef(null);
  // Add a flag for automatic recovery
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  
  // Modify the authentication check to use the same approach as the manual reload
  useEffect(() => {
    // Reset on (re)mount; StrictMode runs cleanup once before remounting
    isMounted.current = true;

    const checkAuth = async () => {
      const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
      
      if (token) {
        setIsLoggedIn(true);
        setIsMember(true);
        
        try {
          // Let's do exactly what the manual reload button does since that works
          const response = await memberService.getProfile();
          
          if (response && isMounted.current) {
            // Lock the form immediately to prevent any resets
            setFormLocked(true);
            
            // Cache the response for future use
            localStorage.setItem('memberData', JSON.stringify(response));
            
            // Set display name immediately
            const memberName = `${response.firstName || ''} ${response.lastName || ''}`.trim();
            setMemberDisplayName(memberName || 'Member');
            
            // Store the original data in a ref so it can't be lost
            originalDataRef.current = {
              name: memberName || '',
              email: response.email || '',
              mobile: response.phone || '',
              address: response.address || '',
              city: response.city || '',
              state: response.state || ''
            };
            
            // Create the form data
            const newFormData = {
              name: memberName || '',
              title: '',
              qualification: '',
              expertise: '',
              email: response.email || '',
              mobile: response.phone || '',
              linkedin: '',
              address: response.address || '',
              city: response.city || '',
              state: response.state || '',
              additionalInfo: ''
            };
            
            
            // Also save a backup to localStorage
            localStorage.setItem('formDataBackup', JSON.stringify(newFormData));
            // Update last saved ref to detect unintentional clears
            lastSavedFormDataRef.current = {...newFormData};
            
            // Use a more aggressive approach with repeated updates
            setFormData(newFormData);
            
            // Use multiple timeouts at different intervals to ensure the data sticks
            setTimeout(() => {
              if (isMounted.current) {
                setFormData(prev => {
                  const updatedData = {...prev, ...newFormData};
                  // Keep backup updated
                  localStorage.setItem('formDataBackup', JSON.stringify(updatedData));
                  return updatedData;
                });
              }
            }, 100);
            
            setTimeout(() => {
              if (isMounted.current) {
                setFormData(prev => {
                  const updatedData = {...prev, ...newFormData};
                  // Keep backup updated
                  localStorage.setItem('formDataBackup', JSON.stringify(updatedData));
                  return updatedData;
                });
                
                // Also directly update DOM as backup
                if (nameInputRef.current) nameInputRef.current.value = newFormData.name;
                if (emailInputRef.current) emailInputRef.current.value = newFormData.email;
                if (mobileInputRef.current) mobileInputRef.current.value = newFormData.mobile;
                if (addressInputRef.current) addressInputRef.current.value = newFormData.address;
                if (cityInputRef.current) cityInputRef.current.value = newFormData.city;
                if (stateInputRef.current) stateInputRef.current.value = newFormData.state;
              }
            }, 500);
            
            // Final update after a longer delay
            setTimeout(() => {
              if (isMounted.current) {
                setFormData(prev => {
                  const updatedData = {...prev, ...newFormData};
                  // Keep backup updated
                  localStorage.setItem('formDataBackup', JSON.stringify(updatedData));
                  return updatedData;
                });
                setInitialDataSet(true);
                setProfileDataLoaded(true);
                setDataWasSuccessfullyLoaded(true);
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error fetching member profile:', error);
          setProfileDataLoaded(true);
          
          // Try to use cached data as fallback if API fails
          const memberDataStr = localStorage.getItem('memberData');
          if (memberDataStr) {
            try {
              const memberData = JSON.parse(memberDataStr);
              
              const memberName = `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim();
              setMemberDisplayName(memberName || 'Member');
              
              const cachedFormData = {
                name: memberName || '',
                title: '',
                qualification: '',
                expertise: '',
                email: memberData.email || '',
                mobile: memberData.phone || '',
                linkedin: '',
                address: memberData.address || '',
                city: memberData.city || '',
                state: memberData.state || '',
                additionalInfo: ''
              };
              
              setTimeout(() => {
                setFormData(cachedFormData);
                setInitialDataSet(true);
              }, 100);
            } catch (cacheError) {
              console.error("Error using cached member data:", cacheError);
            }
          }
        }
        
      } else {
        setIsLoggedIn(false);
        setIsMember(false);
      }
      
      // Always set loading to false at the end
      setIsLoading(false);
    };
    
    checkAuth();
    
    // Cleanup function to prevent updates if component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []); // Empty dependency array to run only once on mount

  // Add an effect to detect and prevent form data from disappearing
  useEffect(() => {
    // If we have data, save it to our ref for comparison
    if (Object.values(formData).some(val => val)) {
      lastSavedFormDataRef.current = {...formData};
    }
    
    // Check if the form unexpectedly cleared (name field is a good indicator)
    if (dataWasSuccessfullyLoaded && 
        !formData.name && 
        lastSavedFormDataRef.current?.name && 
        !recoveryAttempted) {
      
      
      // Try to recover from localStorage backup first
      const backupStr = localStorage.getItem('formDataBackup');
      if (backupStr) {
        try {
          const backupData = JSON.parse(backupStr);
          setFormData(backupData);
          
          // Directly update DOM as backup
          setTimeout(() => {
            if (nameInputRef.current) nameInputRef.current.value = backupData.name || '';
            if (emailInputRef.current) emailInputRef.current.value = backupData.email || '';
            if (mobileInputRef.current) mobileInputRef.current.value = backupData.mobile || '';
            if (addressInputRef.current) addressInputRef.current.value = backupData.address || '';
            if (cityInputRef.current) cityInputRef.current.value = backupData.city || '';
            if (stateInputRef.current) stateInputRef.current.value = backupData.state || '';
          }, 50);
          
          setRecoveryAttempted(true);
          return;
        } catch (e) {
          console.error("Error recovering from backup:", e);
        }
      }
      
      // If backup doesn't work, try ref data
      if (lastSavedFormDataRef.current) {
        setFormData(lastSavedFormDataRef.current);
        
        // Directly update DOM as backup
        setTimeout(() => {
          const data = lastSavedFormDataRef.current;
          if (nameInputRef.current) nameInputRef.current.value = data.name || '';
          if (emailInputRef.current) emailInputRef.current.value = data.email || '';
          if (mobileInputRef.current) mobileInputRef.current.value = data.mobile || '';
          if (addressInputRef.current) addressInputRef.current.value = data.address || '';
          if (cityInputRef.current) cityInputRef.current.value = data.city || '';
          if (stateInputRef.current) stateInputRef.current.value = data.state || '';
        }, 50);
        
        setRecoveryAttempted(true);
      }
    }
  }, [formData, dataWasSuccessfullyLoaded, recoveryAttempted]);

  // Add effect to log when loading state changes
  useEffect(() => {
  }, [isLoading]);

  // Add effect to log form data changes
  useEffect(() => {
  }, [formData]);

  // Separate useEffect for pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await publicService.getProfessionalListingPrice();
        if (isMounted.current) {
          setMemberPrice(response.member_price);
          setNonMemberPrice(response.non_member_price);
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    
    fetchPricing();
  }, []); // Empty dependency array to run only once on mount
  
  // Calculate the price based on membership status
  const getPrice = () => {
    return memberPrice;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If form is locked and trying to clear a prefilled field, prevent it
    if (formLocked && originalDataRef.current && 
        originalDataRef.current[name] && 
        !value.trim() &&
        ['name', 'email', 'mobile', 'address', 'city', 'state'].includes(name)) {
      return;
    }
    
    setFormData(prevData => {
      const newData = {
        ...prevData,
        [name]: value
      };
      
      // Update backup in localStorage whenever form data changes
      localStorage.setItem('formDataBackup', JSON.stringify(newData));
      return newData;
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Add form step state to track the current step
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Function to navigate between form steps
  const handleStepChange = (step) => {
    if (step < 1 || step > totalSteps) return;
    
    // Validate current step before proceeding
    if (step > currentStep) {
      const fieldsToValidate = {
        1: ['name', 'title', 'qualification', 'expertise'],
        2: ['email', 'mobile', 'linkedin'],
        3: ['address', 'city', 'state']
      };
      
      const currentFields = fieldsToValidate[currentStep] || [];
      const stepErrors = {};
      
      currentFields.forEach(field => {
        if (field === 'email' && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
          stepErrors.email = 'Please enter a valid email address';
        } else if (field === 'mobile' && formData.mobile && !/^\d{10}$/.test(formData.mobile)) {
          stepErrors.mobile = 'Please enter a valid 10-digit mobile number';
        } else if (field === 'linkedin' && formData.linkedin && !formData.linkedin.includes('linkedin.com')) {
          stepErrors.linkedin = 'Please enter a valid LinkedIn URL';
        } else if (!formData[field]?.trim() && ['name', 'qualification', 'expertise', 'email', 'mobile', 'linkedin', 'address', 'city', 'state'].includes(field)) {
          stepErrors[field] = 'This field is required';
        }
      });
      
      if (Object.keys(stepErrors).length > 0) {
        setErrors(prevErrors => ({ ...prevErrors, ...stepErrors }));
        return;
      }
    }
    
    // Smooth scroll to top of form
    document.querySelector('.listing-form-container')?.scrollIntoView({ behavior: 'smooth' });
    setCurrentStep(step);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    const requiredFields = [
      'name', 'qualification', 'expertise', 'email', 
      'mobile', 'linkedin', 'address', 'city', 'state'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Mobile validation
    if (formData.mobile && !/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    // LinkedIn URL validation
    if (formData.linkedin && !formData.linkedin.includes('linkedin.com')) {
      newErrors.linkedin = 'Please enter a valid LinkedIn URL';
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First check if the user is eligible (if logged in)
    if (isLoggedIn && !isEligible && eligibilityChecked) {
      setErrors({
        form: eligibilityMessage || 'You are not eligible to create a professional listing.'
      });
      window.scrollTo(0, 0);
      return;
    }
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      
      // Find the first step with errors and navigate to it
      const fieldToStepMap = {
        name: 1, title: 1, qualification: 1, expertise: 1,
        email: 2, mobile: 2, linkedin: 2,
        address: 3, city: 3, state: 3,
        additionalInfo: 4
      };
      
      const errorFields = Object.keys(validationErrors);
      const stepsWithErrors = errorFields.map(field => fieldToStepMap[field]).filter(Boolean);
      if (stepsWithErrors.length) {
        setCurrentStep(Math.min(...stepsWithErrors));
      }
      
      window.scrollTo(0, 0);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Use member service for members, public service for non-members
      let response;
      if (isLoggedIn) {
        response = await memberService.createProfessionalListing(formData);
      } else {
        // For demonstration - in reality, non-members wouldn't create listings
        response = await publicService.createNonMemberProfessionalListing(formData);
      }
      
      if (response.requiresPayment || response.paymentLink) {
        // Store payment info and show payment modal
        setPaymentInfo({
          razorpay_order_id: response.razorpay_order_id || response.payment?.razorpay_order_id,
          razorpay_key_id: response.razorpay_key_id || response.payment?.razorpay_key_id,
          amount: response.amount || response.payment?.amount,
          currency: response.currency || response.payment?.currency || 'INR',
          listing_id: response.listingId || response.payment?.listing_id
        });
        setShowPaymentModal(true);
      } else {
        // If no payment needed, redirect to success page
        navigate('/professionals/listing-success', {
          state: { message: response.message || 'Your professional listing has been submitted successfully.' }
        });
      }
    } catch (error) {
      console.error('Error submitting listing:', error);
      let errorMessage = 'Failed to submit listing. Please try again later.';
      
      // Extract more specific error message if available
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({
        form: errorMessage
      });
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Improve payment error handling
  const handlePayment = () => {
    if (!paymentInfo) {
      setErrors({
        payment: 'Payment information is missing. Please try again.'
      });
      return;
    }
    
    try {
      // Clear any previous payment errors
      setErrors(prev => ({...prev, payment: null}));
      
      const options = {
        key: paymentInfo.razorpay_key_id,
        amount: paymentInfo.amount,
        currency: paymentInfo.currency || 'INR',
        name: 'Bangalore Valuers Association',
        description: 'Professional Listing Fee',
        order_id: paymentInfo.razorpay_order_id,
        handler: async function (response) {
          try {
            // Show processing state
            setErrors({
              payment: null
            });
            setIsSubmitting(true);
            
            // Verify payment
            const verifyData = {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              listing_id: paymentInfo.listing_id
            };
            
            // Use the appropriate service based on user login state
            const verifyResponse = isLoggedIn 
              ? await memberService.verifyProfessionalListingPayment(verifyData)
              : await publicService.verifyProfessionalListingPayment(verifyData);
            
            if (verifyResponse.success) {
              // Redirect to success page
              navigate('/professionals/listing-success', {
                state: { message: 'Your professional listing has been created successfully!' }
              });
            } else {
              setErrors({
                payment: verifyResponse.message || 'Payment verification failed. Please contact support.'
              });
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            let errorMessage = 'Payment verification failed. Please contact support.';
            
            if (error.response?.data?.error) {
              errorMessage = error.response.data.error;
            } else if (error.response?.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error.message) {
              errorMessage = error.message;
            }
            
            setErrors({
              payment: errorMessage
            });
          } finally {
            setIsSubmitting(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.mobile
        },
        notes: {
          listing_id: paymentInfo.listing_id || '',
          member_id: paymentInfo.member_id || ''
        },
        theme: {
          color: '#002147'
        },
        modal: {
          ondismiss: function() {
            // When modal is dismissed, clear submitting state
            setIsSubmitting(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function(response) {
        const error = response.error || {};
        setErrors({
          payment: `Payment failed: ${error.description || error.reason || 'Unknown error'}`
        });
        setIsSubmitting(false);
      });
      
      razorpay.open();
    } catch (error) {
      console.error('Error initializing payment:', error);
      setErrors({
        payment: 'Failed to initialize payment. Please try again later.'
      });
      setIsSubmitting(false);
    }
  };
  
  // Reload form data function
  const reloadFormData = () => {
    
    // Force unlock the form first
    setFormLocked(false);
    
    // Check if we have original data in the ref
    if (originalDataRef.current) {
      const original = originalDataRef.current;
      
      const restoredData = {
        ...formData,
        name: original.name,
        email: original.email,
        mobile: original.mobile,
        address: original.address,
        city: original.city,
        state: original.state
      };
      
      // Update state and DOM directly
      setFormData(restoredData);
      
      // Also directly update DOM
      if (nameInputRef.current) nameInputRef.current.value = original.name;
      if (emailInputRef.current) emailInputRef.current.value = original.email;
      if (mobileInputRef.current) mobileInputRef.current.value = original.mobile;
      if (addressInputRef.current) addressInputRef.current.value = original.address;
      if (cityInputRef.current) cityInputRef.current.value = original.city;
      if (stateInputRef.current) stateInputRef.current.value = original.state;
      
      // Update backup in localStorage
      localStorage.setItem('formDataBackup', JSON.stringify(restoredData));
      
      setInitialDataSet(true);
      setDataWasSuccessfullyLoaded(true);
      setFormLocked(true);
      return;
    }
    
    // Otherwise use the existing function logic
    // Check if we have member data in localStorage
    const memberDataStr = localStorage.getItem('memberData');
    if (memberDataStr) {
      try {
        const memberData = JSON.parse(memberDataStr);
        
        // Use this data to fill the form
        const memberName = `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim();
        
        const cachedFormData = {
          ...formData,
          name: memberName || formData.name,
          email: memberData.email || formData.email,
          mobile: memberData.phone || formData.mobile,
          address: memberData.address || formData.address,
          city: memberData.city || formData.city,
          state: memberData.state || formData.state
        };
        
        setFormData(cachedFormData);
        setInitialDataSet(true);
      } catch (error) {
        console.error("Error parsing cached member data:", error);
      }
    } else {
      // If no cached data, try to reload from API
      memberService.getProfile().then(response => {
        if (response) {
          // Cache the response for future use
          localStorage.setItem('memberData', JSON.stringify(response));
          
          const memberName = `${response.firstName || ''} ${response.lastName || ''}`.trim();
          
          const newFormData = {
            ...formData,
            name: memberName || '',
            email: response.email || '',
            mobile: response.phone || '',
            address: response.address || '',
            city: response.city || '',
            state: response.state || ''
          };
          
          setFormData(newFormData);
          setInitialDataSet(true);
        }
      }).catch(error => {
        console.error("Error reloading profile:", error);
      });
    }
  };
  
  // Profile data loaded effect
  useEffect(() => {
    if (profileDataLoaded && !initialDataSet) {
      // Force a re-render by setting a timeout
      setTimeout(() => {
        
        // Directly set values using refs
        if (nameInputRef.current) {
          nameInputRef.current.value = formData.name;
        }
        if (emailInputRef.current) {
          emailInputRef.current.value = formData.email;
        }
        if (mobileInputRef.current) {
          mobileInputRef.current.value = formData.mobile;
        }
        if (addressInputRef.current) {
          addressInputRef.current.value = formData.address;
        }
        if (cityInputRef.current) {
          cityInputRef.current.value = formData.city;
        }
        if (stateInputRef.current) {
          stateInputRef.current.value = formData.state;
        }
        
        // Mark as done so we don't do this again
        setInitialDataSet(true);
      }, 1000); // 1 second delay
    }
  }, [profileDataLoaded, formData, initialDataSet]);
  
  // Add a component did mount effect to force data load on mount
  useEffect(() => {
    // Force a reload right after component mounts
    const timeoutId = setTimeout(() => {
      if (isLoggedIn && !initialDataSet) {
        reloadFormData();
      }
    }, 800); // Give enough time for component to fully mount
    
    return () => clearTimeout(timeoutId);
  }, [isLoggedIn]);
  
  // Add state for eligibility check
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [eligibilityMessage, setEligibilityMessage] = useState('');
  const [eligibilityError, setEligibilityError] = useState(null);
  
  // Check eligibility when the component mounts
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        // Use PUBLIC_URLS.PROFESSIONAL_LISTING_ELIGIBILITY from apiConfig
        const response = await fetch(PUBLIC_URLS.PROFESSIONAL_LISTING_ELIGIBILITY, {
          headers
        });
        const data = await response.json();
        if (response.ok) {
          setIsEligible(data.eligible);
          setEligibilityMessage(data.message || '');
          setEligibilityError(null);
          setErrors(prev => ({ ...prev, eligibility: data.eligible ? null : data.message }));
        } else {
          setIsEligible(false);
          setEligibilityMessage('');
          setEligibilityError(data.message || 'Eligibility check failed');
          setErrors(prev => ({ ...prev, eligibility: data.message || 'Eligibility check failed' }));
        }
        setEligibilityChecked(true);
      } catch (error) {
        setIsEligible(false);
        setEligibilityMessage('');
        setEligibilityError('Eligibility check failed');
        setErrors(prev => ({ ...prev, eligibility: 'Eligibility check failed' }));
        setEligibilityChecked(true);
      }
    };

    if (isLoggedIn && !eligibilityChecked) {
      checkEligibility();
    }
  }, [isLoggedIn, eligibilityChecked]);

  return (
    <div className="page-container">
      {/* Payment Verification Loading Overlay */}
      {isSubmitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: 'white',
            fontSize: '1.2rem',
            marginTop: '1.5rem',
            fontWeight: '500'
          }}>
            Verifying your payment...
          </p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Please wait, this may take a few moments
          </p>
        </div>
      )}
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <Header />
      
      <div className="page-header">
        <h1>ADD YOUR PROFESSIONAL LISTING</h1>
        <p>Get featured in our directory of valuation professionals</p>
      </div>
      
      <div className="add-listing-container">
        {/* Show eligibility error message at the top if present */}
        {errors.eligibility && (
          <div className="eligibility-error alert alert-warning">
            <i className="fa fa-exclamation-triangle"></i> {errors.eligibility}
            {errors.eligibility.includes('membership') && (
              <div className="mt-2">
                <button 
                  className="btn btn-primary btn-sm" 
                  onClick={() => navigate('/member/profile')}
                >
                  Renew Membership
                </button>
              </div>
            )}
          </div>
        )}

        {/* Only show the rest of the content if eligibility is not failed */}
        {(!errors.eligibility || isEligible) && (
          <>
            {/* Progress Indicator */}
            <div className="form-progress-container">
              <div className="form-progress">
                {[...Array(totalSteps)].map((_, idx) => (
                  <div 
                    key={idx} 
                    className={`progress-step ${currentStep > idx + 1 ? 'completed' : ''} ${currentStep === idx + 1 ? 'active' : ''}`}
                    onClick={() => handleStepChange(idx + 1)}
                  >
                    <div className="step-number">{idx + 1}</div>
                    <div className="step-label">
                      {idx === 0 ? 'Professional Info' :
                       idx === 1 ? 'Contact' :
                       idx === 2 ? 'Location' :
                       'Additional Info'}
                    </div>
                  </div>
                ))}
                <div className="progress-bar">
                  <div 
                    className="progress-completed"
                    style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
      
            <div className="listing-content">
              {/* Price and Info Card */}
              <div className="listing-price-card">
                <div className="price-header">Professional Listing Fee</div>
                <div className="price-amount">{getPrice() > 0 ? `₹${getPrice().toLocaleString('en-IN')}` : 'Free'}</div>
                
                {isLoggedIn ? (
                  <div className="logged-in-price-info">
                    <div className="member-price-info">
                      <div className="member-badge">
                        <i className="fa fa-star"></i> Member Price
                      </div>
                      <p>You're getting our special member rate!</p>
                    </div>
                    <div className="user-account-info">
                      <p>
                        <i className="fa fa-user-circle"></i> {' '}
                        Logged in as: <strong>{memberDisplayName}</strong>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="price-comparison-info">
                    <p>
                      <strong>Members pay only ₹{memberPrice.toLocaleString('en-IN')}</strong>
                      <br/>Save ₹{(nonMemberPrice - memberPrice).toLocaleString('en-IN')} with a membership!
                    </p>
                    <div className="login-options">
                      <button 
                        className="btn btn-primary login-btn"
                        onClick={() => navigate('/login')}
                      >
                        Login
                      </button>
                      <button 
                        className="btn btn-outline-primary membership-btn"
                        onClick={() => navigate('/membership')}
                      >
                        Get Membership
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="price-benefits">
                  <h4>{isLoggedIn ? "Your Benefits:" : "Benefits Include:"}</h4>
                  <ul className="benefits-list">
                    <li><span className="benefit-icon">✓</span> Professional profile on our platform</li>
                    <li><span className="benefit-icon">✓</span> Visibility to potential clients</li>
                    <li><span className="benefit-icon">✓</span> Connect with other professionals</li>
                    <li><span className="benefit-icon">✓</span> Valid for 1 year</li>
                    <li><span className="benefit-icon">✓</span> Priority response for inquiries</li>
                    {isLoggedIn && (
                      <>
                        <li><span className="benefit-icon">✓</span> Featured in member directory</li>
                        <li><span className="benefit-icon">✓</span> Early access to industry reports</li>
                      </>
                    )}
                  </ul>
                  
                  {isLoggedIn && (
                    <div className="listing-info-note">
                      <p><i className="fa fa-info-circle"></i> Your listing will be active for 1 year from the date of approval.</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Form */}
              <div className="listing-form-container">
                {errors.form && (
                  <div className="form-error alert alert-danger">
                    {errors.form}
                  </div>
                )}
                
                {isLoading ? (
                  <div className="loading-container">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading your profile information...</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="listing-form" noValidate>
                    {/* Step 1: Professional Information */}
                    <div className={`form-step ${currentStep === 1 ? 'active' : ''}`}>
                      <h2>Professional Information</h2>
                      <div className="form-section">
                        <div className="form-group">
                          <label htmlFor="name">Display Name <span className="required">*</span></label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            ref={nameInputRef}
                            value={formData.name}
                            onChange={handleChange}
                            className={errors.name ? 'has-error' : ''}
                          />
                          {errors.name && <div className="error-message">{errors.name}</div>}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="title">Professional Title</label>
                          <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="e.g., Registered Valuer, Financial Consultant"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="qualification">Qualifications <span className="required">*</span></label>
                          <input
                            type="text"
                            id="qualification"
                            name="qualification"
                            value={formData.qualification}
                            onChange={handleChange}
                            className={errors.qualification ? 'has-error' : ''}
                            placeholder="e.g., CA, CFA, MBA Finance"
                          />
                          {errors.qualification && <div className="error-message">{errors.qualification}</div>}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="expertise">Areas of Expertise <span className="required">*</span></label>
                          <input
                            type="text"
                            id="expertise"
                            name="expertise"
                            value={formData.expertise}
                            onChange={handleChange}
                            className={errors.expertise ? 'has-error' : ''}
                            placeholder="e.g., Business Valuation, Financial Assets"
                          />
                          {errors.expertise && <div className="error-message">{errors.expertise}</div>}
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 2: Contact Information */}
                    <div className={`form-step ${currentStep === 2 ? 'active' : ''}`}>
                      <h2>Contact Information</h2>
                      <div className="form-section">
                        <div className="form-group">
                          <label htmlFor="email">Email Address <span className="required">*</span></label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            ref={emailInputRef}
                            value={formData.email}
                            onChange={handleChange}
                            className={errors.email ? 'has-error' : ''}
                          />
                          {errors.email && <div className="error-message">{errors.email}</div>}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="mobile">Mobile Number <span className="required">*</span></label>
                          <div className="input-with-prefix">
                            <span className="input-prefix">+91</span>
                            <input
                              type="tel"
                              id="mobile"
                              name="mobile"
                              ref={mobileInputRef}
                              value={formData.mobile}
                              onChange={handleChange}
                              className={errors.mobile ? 'has-error' : ''}
                              placeholder="10-digit number"
                            />
                          </div>
                          {errors.mobile && <div className="error-message">{errors.mobile}</div>}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="linkedin">LinkedIn Profile URL <span className="required">*</span></label>
                          <div className="input-with-icon">
                            <i className="fa fa-linkedin input-icon"></i>
                            <input
                              type="url"
                              id="linkedin"
                              name="linkedin"
                              value={formData.linkedin}
                              onChange={handleChange}
                              className={errors.linkedin ? 'has-error' : ''}
                              placeholder="https://www.linkedin.com/in/your-profile"
                            />
                          </div>
                          {errors.linkedin && <div className="error-message">{errors.linkedin}</div>}
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 3: Location Details */}
                    <div className={`form-step ${currentStep === 3 ? 'active' : ''}`}>
                      <h2>Location Details</h2>
                      <div className="form-section">
                        <div className="form-group">
                          <label htmlFor="address">Address <span className="required">*</span></label>
                          <textarea
                            id="address"
                            name="address"
                            ref={addressInputRef}
                            value={formData.address}
                            onChange={handleChange}
                            className={errors.address ? 'has-error' : ''}
                            rows="3"
                          ></textarea>
                          {errors.address && <div className="error-message">{errors.address}</div>}
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="city">City <span className="required">*</span></label>
                            <input
                              type="text"
                              id="city"
                              name="city"
                              ref={cityInputRef}
                              value={formData.city}
                              onChange={handleChange}
                              className={errors.city ? 'has-error' : ''}
                            />
                            {errors.city && <div className="error-message">{errors.city}</div>}
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor="state">State <span className="required">*</span></label>
                            <input
                              type="text"
                              id="state"
                              name="state"
                              ref={stateInputRef}
                              value={formData.state}
                              onChange={handleChange}
                              className={errors.state ? 'has-error' : ''}
                            />
                            {errors.state && <div className="error-message">{errors.state}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 4: Additional Information */}
                    <div className={`form-step ${currentStep === 4 ? 'active' : ''}`}>
                      <h2>Additional Information</h2>
                      <div className="form-section">
                        <div className="form-group">
                          <label htmlFor="additionalInfo">Additional Details (Optional)</label>
                          <textarea
                            id="additionalInfo"
                            name="additionalInfo"
                            value={formData.additionalInfo}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Provide any additional information about your services, experience, or specialization"
                          ></textarea>
                        </div>
                      </div>
                      
                      <div className="form-summary">
                        <h3>Summary</h3>
                        <div className="summary-section">
                          <div className="summary-item">
                            <span className="summary-label">Name:</span>
                            <span className="summary-value">{formData.name || '—'}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Email:</span>
                            <span className="summary-value">{formData.email || '—'}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Mobile:</span>
                            <span className="summary-value">{formData.mobile || '—'}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Location:</span>
                            <span className="summary-value">{formData.city ? `${formData.city}, ${formData.state}` : '—'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-navigation">
                      {currentStep > 1 && (
                        <button 
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => handleStepChange(currentStep - 1)}
                        >
                          Previous
                        </button>
                      )}
                      
                      {currentStep < totalSteps ? (
                        <button 
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleStepChange(currentStep + 1)}
                        >
                          Next
                        </button>
                      ) : (
                        <div className="form-actions">
                          <button 
                            type="button" 
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/professionals')}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </button>
                          <button 
                            type="submit" 
                            className="btn btn-primary submit-btn"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Submitting...' : (
                              <>
                                <span>{getPrice() > 0 ? 'Submit & Pay' : 'Submit'}</span>
                                <span className="price-badge">{getPrice() > 0 ? `₹${getPrice().toLocaleString('en-IN')}` : 'Free'}</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </form>
                )}
                
                {/* Automatic recovery message */}
                {recoveryAttempted && (
                  <div className="alert alert-info" style={{marginTop: '20px'}}>
                    <strong>Notice:</strong> We detected that your form data disappeared and automatically recovered it.
                    If you're still having issues, please use the "Force Reload Data" button.
                  </div>
                )}
                
              </div>
            </div>
          </>
        )}
      </div>
      
      {showPaymentModal && (
        <div className="payment-modal-backdrop">
          <div className="payment-modal">
            <div className="payment-modal-header">
              <h3>Complete Payment</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPaymentModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="payment-modal-body">
              <div className="payment-success-icon">
                <i className="fa fa-check-circle"></i>
              </div>
              <p className="payment-modal-message">Your listing information has been saved. Complete the payment to publish your listing.</p>
              <div className="payment-info">
                <div className="payment-amount">
                  <span>Amount:</span>
                  <span className="amount-value">₹{(paymentInfo?.amount / 100).toLocaleString('en-IN')}</span>
                </div>
                <div className="payment-details">
                  <div className="payment-detail-item">
                    <span>Order ID:</span>
                    <span>{paymentInfo?.razorpay_order_id}</span>
                  </div>
                  <div className="payment-detail-item">
                    <span>Listing Type:</span>
                    <span>Professional Listing (1 Year)</span>
                  </div>
                </div>
              </div>
              {errors.payment && (
                <div className="payment-error alert alert-danger">
                  {errors.payment}
                </div>
              )}
            </div>
            <div className="payment-modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowPaymentModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success pay-btn" 
                onClick={handlePayment}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-credit-card"></i> Proceed to Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default AddProfessionalListing;
